/**
 * Editing one group, and the write (ORDR-119, item 5; seam C1 of w2c-ord-config).
 *
 * START FROM THE VALUE IN FORCE. The GET's `value` is the RESOLVED value,
 * possibly an ancestor's; the edit starts from it, and the PUT sends the WHOLE
 * object, because the server REPLACES this node's own value. Keys this editor
 * does not show (the rest of CAPACITY in the kitchen) are sent back unchanged.
 *
 * SAY WHAT A SAVE DOES BEFORE IT HAPPENS, inline, not in a dialog. Saving at a
 * node that inherits breaks inheritance for the whole domain, so that case
 * needs a confirmation (C-HQ4).
 *
 * THE REPLY IS THE TRUTH. On 200 or 201 the group re-renders from the
 * response, never from what was sent. On any refusal the edit is KEPT.
 */

import { useState, type ReactElement } from "react";
import type { ConfigApi, ConfigReading, ConfigValue } from "./configApi.js";
import type { ConfigDomain } from "./domains.js";
import { TYPED_FIELDS, checkField } from "./fields.js";
import type { ConfigScope } from "./index.js";
import { saveConsequence } from "./provenance.js";
import { describeRefusal, type RefusalView } from "./refusal.js";
import { TypedControl } from "./TypedControl.js";

export interface EditorProps {
  readonly domain: ConfigDomain;
  readonly reading: ConfigReading;
  readonly scope: ConfigScope;
  readonly api: ConfigApi;
  /** The keys this editor owns; every other key is sent back unchanged. */
  readonly keys: (key: string) => boolean;
  /** No free JSON: only the typed controls (the kitchen's compact form). */
  readonly typedOnly: boolean;
  readonly onSaved: (reading: ConfigReading) => void;
  /** A refusal that changes the group itself: read-only, or locked above. */
  readonly onRefused: (view: RefusalView) => void;
  readonly onCancel: () => void;
}

type Parsed = { readonly ok: true; readonly value: ConfigValue } | { readonly ok: false };

function parseObject(text: string): Parsed {
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? { ok: true, value: value as ConfigValue }
      : { ok: false };
  } catch {
    return { ok: false };
  }
}

export function Editor(props: EditorProps): ReactElement {
  const { domain, reading, scope, api, keys, typedOnly } = props;
  const base: ConfigValue = reading.value ?? {};
  const rules = (TYPED_FIELDS[domain] ?? []).filter((rule) => keys(rule.key));
  const typedKeys = new Set(rules.map((rule) => rule.key));
  const owned = (key: string): boolean => keys(key) && (typedKeys.has(key) || !typedOnly);

  const [typed, setTyped] = useState<Record<string, string>>(() =>
    Object.fromEntries(rules.map((rule) => [rule.key, base[rule.key] === undefined ? "" : String(base[rule.key])])),
  );
  const [json, setJson] = useState(() =>
    JSON.stringify(
      Object.fromEntries(Object.entries(base).filter(([key]) => keys(key) && !typedKeys.has(key))),
      null,
      2,
    ),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<RefusalView | null>(null);

  const checks = rules.map((rule) => ({
    rule,
    raw: typed[rule.key] ?? "",
    check: (typed[rule.key] ?? "") === "" ? null : checkField(rule, typed[rule.key] ?? ""),
  }));
  const parsed: Parsed = typedOnly ? { ok: true, value: {} } : parseObject(json);
  const consequence = saveConsequence(reading, scope.nodeId, scope.nodeName, domain);
  const valid = parsed.ok && checks.every((c) => c.check === null || c.check.ok);
  const canSave = valid && !busy && (!consequence.needsConfirm || confirmed);

  const save = async (): Promise<void> => {
    if (!parsed.ok) return;
    const kept = Object.fromEntries(Object.entries(base).filter(([key]) => !owned(key)));
    const fromJson = Object.fromEntries(Object.entries(parsed.value).filter(([key]) => !typedKeys.has(key)));
    const fromTyped = Object.fromEntries(
      checks.flatMap((c) => (c.check !== null && c.check.ok ? [[c.rule.key, c.check.value]] : [])),
    );
    setBusy(true);
    setRefusal(null);
    const outcome = await api.write(scope.nodeId, domain, { ...kept, ...fromJson, ...fromTyped });
    setBusy(false);
    if (outcome.ok) {
      props.onSaved(outcome.reading);
      return;
    }
    const view = describeRefusal(outcome, domain);
    if (view.removeControl || view.reload) {
      props.onRefused(view);
      return;
    }
    setRefusal(view);
  };

  const fieldError = (key: string): string | null => (refusal?.field === key ? refusal.message : null);

  return (
    <form
      data-testid={`editor-${domain}`}
      className="mt-2 flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {checks.map(({ rule, raw, check }) => (
        <TypedControl
          key={rule.key}
          rule={rule}
          raw={raw}
          problem={check !== null && !check.ok ? check.message : fieldError(rule.key)}
          onChange={(next) => setTyped((prev) => ({ ...prev, [rule.key]: next }))}
        />
      ))}

      {!typedOnly && (
        <label className="text-sm">
          Other {domain} settings (JSON object)
          <textarea
            aria-label={`${domain} settings as JSON`}
            value={json}
            onChange={(event) => setJson(event.target.value)}
            rows={6}
            className="mt-1 w-full rounded border border-[var(--color-neutral-300)] p-2 font-mono text-xs"
          />
          {!parsed.ok && <span role="alert"> Settings must be a JSON object.</span>}
        </label>
      )}
      {refusal?.field !== null && refusal?.field !== undefined && !typedKeys.has(refusal.field) && (
        <p role="alert" className="text-sm">
          {refusal.field}: {refusal.message}
        </p>
      )}

      {consequence.sentence !== null && (
        <p data-testid="save-consequence" className="text-sm font-medium">
          {consequence.sentence}
        </p>
      )}
      {consequence.needsConfirm && (
        <label className="text-sm">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />{" "}
          I understand. Save {domain} here anyway.
        </label>
      )}

      {refusal !== null && refusal.field === null && (
        <p role="alert" data-testid="save-refusal" className="text-sm">
          {refusal.message}
        </p>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={!canSave} className="rounded bg-[var(--color-primary-700)] px-3 py-1 text-sm text-white disabled:opacity-60">
          Save
        </button>
        <button type="button" onClick={props.onCancel} className="rounded border px-3 py-1 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}
